import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { forwardRef } from 'preact/compat';
import { BRAND_CLASSES } from '@/constants/theme';
import { CharCounter } from './CharCounter';
/**
 * TextBox component with React Hook Form integration and character counter support
 *
 * @example
 * // Basic usage with React Hook Form
 * const { register, formState: { errors } } = useForm()
 *
 * <TextBox
 *   label="Name"
 *   required
 *   error={errors.name?.message}
 *   showCharCounter
 *   maxLength={200}
 *   {...register('name', { required: 'Name is required', maxLength: 200 })}
 * />
 *
 * @example
 * // Multiline with character counter
 * <TextBox
 *   label="Description"
 *   multiline
 *   rows={4}
 *   showCharCounter
 *   maxLength={1000}
 *   {...register('description')}
 * />
 */
export const TextBox = forwardRef(({ label, required = false, name, error, helperText, multiline = false, rows = 3, showCharCounter = false, maxLength, value, warnAt = 0.8, className = '', containerClassName = '', labelSize = 'sm', labelClassName = '', placeholder, onChange, onBlur, ...props }, ref) => {
    const hasError = !!error;
    const inputClasses = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${hasError
        ? `${BRAND_CLASSES.INPUT.ERROR}`
        : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`} ${BRAND_CLASSES.INPUT.PLACEHOLDER} ${multiline ? 'resize-y' : ''} ${className}`;
    const labelSizeClasses = labelSize === 'md' ? 'text-sm' : 'text-sm';
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { htmlFor: name, className: `block ${labelSizeClasses} font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-2 ${labelClassName}`, children: [label, required && _jsx("span", { className: `${BRAND_CLASSES.INPUT.REQUIRED} ml-1`, children: "*" })] })), multiline ? (_jsx("textarea", { id: name, name: name, rows: rows, maxLength: maxLength, placeholder: placeholder, onChange: onChange, onBlur: onBlur, className: inputClasses, ref: ref, ...props })) : (_jsx("input", { id: name, name: name, type: "text", maxLength: maxLength, placeholder: placeholder, onChange: onChange, onBlur: onBlur, className: inputClasses, ref: ref, ...props })), showCharCounter && maxLength && (_jsx(CharCounter, { value: value, max: maxLength, warnAt: warnAt })), error && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), helperText && !error && (_jsx("p", { className: `mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] }));
});
TextBox.displayName = 'TextBox';
